import React, { useRef } from "react";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import { BaseModal } from "@whatslly/ui-components/modals/BaseModal";
import { IModalInstance, ModalHeader } from "@whatslly/ui-components/modals";
import { ModalButtons } from "@whatslly/ui-components/ModalButtons";
import { FormElement } from "@whatslly/ui-components/v2/FormElements/Form";
import { FormCheckBox } from "@whatslly/ui-components/v2/FormElements/FormCheckBox";
import { Label } from "@whatslly/ui-components/v2/BaseElements/Label";
import type { TemplateLanguages } from "@whatslly/types/waba/WhatsappBusinessApiTemplate";
import { TemplatePurpose } from "@whatslly/types/waba/WhatsappBusinessApiTemplate";
import { FormikProps } from "formik";
import styles from "./EditTemplateModal.module.scss";

type FormValues = {
  isOptIn: boolean;
  isCampaign: boolean;
};

type EditTemplatePurposeModalProps = IModalInstance<{ purposes: TemplatePurpose[] }> & {
  template: TemplateLanguages;
  initialPurposes: TemplatePurpose[];
};

export const EditTemplatePurposeModal: React.FC<EditTemplatePurposeModalProps> = observer(({ closeModal, initialPurposes }) => {
  const { t } = useTranslation();
  const formikRef = useRef<FormikProps<FormValues>>(null);

  const handleSubmit = (values: FormValues) => {
    const purposes: TemplatePurpose[] = [];
    if (values.isOptIn) purposes.push(TemplatePurpose.OPT_IN);
    if (values.isCampaign) purposes.push(TemplatePurpose.CAMPAIGN);
    closeModal({ purposes });
  };

  return (
    <BaseModal
      testId="edit-template-purpose-modal"
      header={<ModalHeader closeModal={() => closeModal()}>{t("whatsapp_business_api_settings_page.edit_template.purpose_title")}</ModalHeader>}
      closeModal={closeModal}
      closeOnClickOutside={false}
      closeOnEscape={false}
      className={styles.editModal}
      bodyClassName={styles.editModalBody}
    >
      <FormElement
        initialValues={{
          isOptIn: initialPurposes.includes(TemplatePurpose.OPT_IN),
          isCampaign: initialPurposes.includes(TemplatePurpose.CAMPAIGN),
        }}
        onSubmit={handleSubmit}
        formikRef={formikRef}
      >
        <div className={styles.editModalContent}>
          <div className={styles.field}>
            <Label text={t("whatsapp_business_api_settings_page.edit_template.purpose_label")} />
            <div className={styles.purposeOptions}>
              <div className={styles.purposeOption}>
                <FormCheckBox name="isOptIn" paddingInline="none" data-testid="edit-template-purpose-opt-in" />
                <span>{t("whatsapp_business_api_settings_page.edit_template.purpose_opt_in")}</span>
              </div>
              <div className={styles.purposeOption}>
                <FormCheckBox name="isCampaign" paddingInline="none" data-testid="edit-template-purpose-campaign" />
                <span>{t("whatsapp_business_api_settings_page.edit_template.purpose_campaign")}</span>
              </div>
            </div>
          </div>
        </div>
        <ModalButtons
          buttonType="button"
          testId="edit-template-purpose-save-button"
          submitText={t("destinations_page.save")}
          onSubmit={() => formikRef.current?.submitForm()}
          onCancel={() => closeModal()}
        />
      </FormElement>
    </BaseModal>
  );
});
